import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import {
  Task,
  TaskInsert,
  TaskUpdate,
  TaskDocProfile
} from '../models/types';

/**
 * Service for task management operations
 */
export class TaskService {
  /**
   * Create a new task
   * @param task The task data to create
   * @returns The created task or null if there was an error
   */
  async createTask(task: TaskInsert): Promise<Task | null> {
    try {
      logger.info('Creating task', LogCategory.DATABASE, { task });
      
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...task,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      logger.info('Task created successfully', LogCategory.DATABASE, { task: data.id });
      
      return data;
    } catch (error) {
      logger.error('Error creating task', LogCategory.DATABASE, { task, error });
      throw error;
    }
  }

  /**
   * Fetch all tasks from the task_doc_profile view
   * @returns Array of tasks with document and user information
   */
  async fetchTasks(): Promise<TaskDocProfile[]> {
    try {
      logger.info('Fetching tasks from task_doc_profile view', LogCategory.DATABASE);
      const { data, error } = await supabase
        .from('task_doc_profile')
        .select('*')
        .order('due_at', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching tasks from task_doc_profile:', LogCategory.DATABASE, { error });

      throw error;
    }
  }

  /**
   * Fetch a single task by ID
   * @param id The task ID to fetch
   * @returns The task or null if not found
   */
  async fetchTaskById(id: string): Promise<TaskDocProfile | null> {
    try {
      const { data, error } = await supabase
        .from('task_doc_profile')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }
      
      return data;
    } catch (error) {
      logger.error('Error fetching task by ID', LogCategory.DATABASE, {
        id,
        error
      });
      
      if (error instanceof Error && error.message.includes('PGRST116')) {
        return null;
      }
      
      throw error;
    }
  }

  /**
   * Update an existing task
   * @param id The ID of the task to update
   * @param updates The task data to update
   * @returns The updated task or null if there was an error
   */
  async updateTask(id: string, updates: TaskUpdate): Promise<Task | null> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      logger.error('Error updating document', LogCategory.DATABASE, {
        id,
        updates,
        error
      });
      throw error;
    }
  }

  /**
   * Mark a task as complete
   * @param id The ID of the task to mark as complete
   * @returns The updated task or null if there was an error
   */
  async completeTask(id: string): Promise<Task | null> {
    return this.updateTask(id, {
      done_at: new Date().toISOString()
    });
  }

  /**
   * Mark a task as incomplete (reopen)
   * @param id The ID of the task to mark as incomplete
   * @returns The updated task or null if there was an error
   */
  async reopenTask(id: string): Promise<Task | null> {
    return this.updateTask(id, {
      done_at: null
    });
  }

  /**
   * Delete a task
   * @param id The ID of the task to delete
   * @returns True if successful, false otherwise
   */
  async deleteTask(id: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      logger.error('Error deleting task', LogCategory.DATABASE, {
        id,
        error
      });
      throw error;
    }
  }

  /**
   * Get tasks by document ID
   * @param documentId The document ID
   * @returns Array of tasks
   */
  async getTasksByDocumentId(documentId: string): Promise<TaskDocProfile[]> {
    try {
      const { data, error } = await supabase
        .from('task_doc_profile')
        .select('*')
        .eq('document_id', documentId)
        .order('due_at', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching tasks by document ID', LogCategory.DATABASE, {
        documentId,
        error
      });
      throw error;
    }
  }

  /**
   * Get tasks by assigned user ID
   * @param userId The user ID
   * @returns Array of tasks
   */
  async getTasksByUserId(userId: string): Promise<TaskDocProfile[]> {
    try {
      const { data, error } = await supabase
        .from('task_doc_profile')
        .select('*')
        .eq('assigned_user_id', userId)
        .order('due_at', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching tasks by user ID', LogCategory.DATABASE, {
        userId,
        error
      });
      throw error;
    }
  }

  /**
   * Get overdue tasks
   * @returns Array of overdue tasks
   */
  async getOverdueTasks(): Promise<TaskDocProfile[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data, error } = await supabase
        .from('task_doc_profile')
        .select('*')
        .lt('due_at', today.toISOString())
        .is('done_at', null)
        .order('due_at', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching overdue tasks', LogCategory.DATABASE, {
        error
      });
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for tasks
   * @param callback Function to call when tasks are updated
   * @returns The subscription that can be used to unsubscribe
   */
  subscribeToTasks(callback: (payload: any) => void) {
    return supabase
      .channel('tasks-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        callback
      )
      .subscribe();
  }
}